/**
 * OnboardingSlidesData.jsx
 * =========================
 *
 * Contenu des 15 slides de l'onboarding client Baobab Connect.
 * Chaque slide est un composant JSX autonome, rendu plein écran par
 * <OnboardingSlideshow />.
 *
 * Palette inspirée du PPTX de référence :
 *   - Background crème : #FAF8F1
 *   - Vert primaire    : #064E3B (emerald-900)
 *   - Vert secondaire  : #047857 (emerald-700)
 *   - Or accent        : #D97706 (amber-600)
 *   - Slate texte      : #334155 (slate-700)
 */
import React from 'react';
import {
  Network, Shield, Radio,
  CheckCircle2, Calendar, Truck, FileCheck2, Receipt,
  Mail, LogIn, Smartphone,
  LayoutDashboard, BarChart3,
  FileText, Clock, Package, MapPin,
  CheckCheck, XCircle, RefreshCw,
  Satellite, BellRing, HandHeart,
  ClipboardSignature, AlertTriangle, ScaleIcon,
  MessageSquare, FolderArchive, Image as ImageIcon,
  Banknote, TrendingUp, Wallet,
  TabletSmartphone, Camera, PenLine,
  PhoneCall, Zap, Scale,
  LineChart, Sparkles, Target,
  Quote,
} from 'lucide-react';

// URL backend pour servir les illustrations
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
export const ASSET = (name) => `${BACKEND_URL}/api/uploads/docs/html_assets/client/${name}`;
export const ADMIN_ASSET = (name) => `${BACKEND_URL}/api/uploads/docs/html_assets/admin/${name}`;

// -------------------------------------------------------------------
// Sous-composants visuels réutilisables (exportés pour le guide admin)
// -------------------------------------------------------------------

export const SlideShell = ({ children, className = '', testId, dark = false }) => (
  <div
    data-testid={testId}
    className={`relative w-full h-full flex flex-col ${
      dark ? 'bg-[#064E3B] text-[#FAF8F1]' : 'bg-[#FAF8F1] text-[#0F172A]'
    } ${className}`}
  >
    {children}
  </div>
);

export const SlideHeader = ({ eyebrow, title, subtitle, dark = false }) => (
  <header className="px-12 md:px-20 pt-12 md:pt-16 pb-4">
    {eyebrow && (
      <div
        className={`text-xs md:text-sm font-bold tracking-[0.18em] uppercase mb-3 ${
          dark ? 'text-[#FCD34D]' : 'text-[#047857]'
        }`}
      >
        {eyebrow}
      </div>
    )}
    <h2
      className={`text-3xl md:text-5xl font-extrabold leading-tight tracking-tight ${
        dark ? 'text-white' : 'text-[#0F172A]'
      }`}
      style={{ fontFamily: 'Georgia, "Playfair Display", serif' }}
    >
      {title}
    </h2>
    {subtitle && (
      <p
        className={`mt-4 md:mt-5 text-lg md:text-xl max-w-3xl leading-relaxed ${
          dark ? 'text-[#A7F3D0]' : 'text-[#475569]'
        }`}
      >
        {subtitle}
      </p>
    )}
    <div className={`mt-6 h-[3px] w-16 ${dark ? 'bg-[#FCD34D]' : 'bg-[#D97706]'}`} />
  </header>
);

export const PillarCard = ({ Icon, accent, badge, title, body }) => (
  <div className="flex-1 bg-white/95 border border-[#064E3B]/10 rounded-2xl p-6 md:p-7 shadow-sm hover:shadow-md transition-shadow">
    <div
      className="w-14 h-14 rounded-xl flex items-center justify-center mb-5"
      style={{ backgroundColor: `${accent}15`, color: accent }}
    >
      <Icon className="w-7 h-7" strokeWidth={2.2} />
    </div>
    {badge && (
      <div className="text-xs font-bold tracking-wider uppercase mb-2" style={{ color: accent }}>
        {badge}
      </div>
    )}
    <h3 className="text-xl md:text-2xl font-bold text-[#0F172A] mb-3 leading-snug">{title}</h3>
    <p className="text-[15px] md:text-base text-[#475569] leading-relaxed">{body}</p>
  </div>
);

export const StepBubble = ({ Icon, num, title, sub, color = '#047857' }) => (
  <div className="flex flex-col items-center text-center flex-1 min-w-0">
    <div
      className="w-20 h-20 md:w-24 md:h-24 rounded-full flex items-center justify-center mb-3 border-2"
      style={{ borderColor: color, color: color, backgroundColor: `${color}10` }}
    >
      <Icon className="w-9 h-9 md:w-11 md:h-11" strokeWidth={2} />
    </div>
    <div className="text-xs font-bold tracking-wider uppercase mb-1" style={{ color }}>
      Étape {num}
    </div>
    <div className="font-bold text-base md:text-lg text-[#0F172A]">{title}</div>
    {sub && <div className="text-sm text-[#64748B] mt-1">{sub}</div>}
  </div>
);

export const Footnote = ({ children, dark = false }) => (
  <div
    className={`absolute bottom-6 left-12 md:left-20 right-12 md:right-20 flex justify-between items-center text-xs ${
      dark ? 'text-[#A7F3D0]/70' : 'text-[#64748B]'
    }`}
  >
    <div className="flex items-center gap-3 font-semibold tracking-wide uppercase">
      <img
        src={ASSET('baobab_logo.png')}
        alt="Baobab Connect"
        className="w-7 h-7 object-contain"
        loading="lazy"
      />
      <span>Baobab Connect · Onboarding Client</span>
    </div>
    <div className="tabular-nums">© 2026</div>
  </div>
);

const ScreenshotFrame = ({ src, alt, caption }) => (
  <div className="relative w-full">
    <div className="rounded-xl overflow-hidden border border-[#064E3B]/15 shadow-lg bg-white">
      <div className="flex items-center gap-1.5 px-3 py-2 bg-[#F1F5F9] border-b border-[#064E3B]/10">
        <span className="w-2.5 h-2.5 rounded-full bg-[#EF4444]/70" />
        <span className="w-2.5 h-2.5 rounded-full bg-[#F59E0B]/70" />
        <span className="w-2.5 h-2.5 rounded-full bg-[#10B981]/70" />
        <span className="ml-3 text-[10px] text-[#94A3B8] font-mono truncate">app.baobabco.net</span>
      </div>
      <img src={src} alt={alt} className="w-full h-auto block" loading="lazy" />
    </div>
    {caption && (
      <div className="mt-2 text-xs text-center text-[#64748B] italic">{caption}</div>
    )}
  </div>
);
export { ScreenshotFrame };

// -------------------------------------------------------------------
// SLIDE 1 — Couverture
// -------------------------------------------------------------------
const Slide1 = () => (
  <SlideShell testId="slide-1" dark className="overflow-hidden">
    {/* Texture subtile */}
    <div
      className="absolute inset-0 opacity-[0.04]"
      style={{
        backgroundImage:
          'radial-gradient(circle at 25% 30%, #FCD34D 0%, transparent 50%), radial-gradient(circle at 80% 70%, #A7F3D0 0%, transparent 45%)',
      }}
    />
    <div className="relative z-10 flex-1 grid md:grid-cols-[1fr_auto] gap-12 items-center px-12 md:px-24">
      <div>
        <div className="text-sm md:text-base font-bold tracking-[0.3em] uppercase text-[#FCD34D] mb-6">
          Formation d'onboarding · Client B2B
        </div>
        <h1
          className="text-4xl md:text-6xl lg:text-7xl font-extrabold leading-[1.05] tracking-tight text-white max-w-4xl"
          style={{ fontFamily: 'Georgia, "Playfair Display", serif' }}
        >
          Bienvenue sur votre <span className="text-[#FCD34D]">Tour de Contrôle</span> Numérique.
        </h1>
        <p className="mt-8 text-xl md:text-2xl text-[#A7F3D0] max-w-3xl leading-relaxed">
          Baobab Connect — La logistique intelligente, conçue pour l'Afrique.
        </p>
        <div className="mt-12 flex items-center gap-6 text-[#A7F3D0]/80 text-sm">
          <span className="font-mono">v3.0 · Mai 2026</span>
          <span>·</span>
          <span>≈ 8 min de lecture</span>
          <span>·</span>
          <span>15 slides</span>
        </div>
      </div>
      {/* Logo en grand à droite */}
      <div className="hidden md:flex flex-col items-center justify-center">
        <div className="w-64 h-64 lg:w-80 lg:h-80 rounded-3xl bg-white/95 p-6 shadow-2xl flex items-center justify-center">
          <img
            src={ASSET('baobab_logo.png')}
            alt="Baobab Connect"
            className="w-full h-full object-contain"
          />
        </div>
      </div>
    </div>
    <div className="absolute bottom-6 left-12 md:left-24 right-12 md:right-24 flex justify-between items-center text-xs text-[#A7F3D0]/70">
      <div className="font-semibold tracking-wide uppercase">Baobab Connect · Onboarding Client</div>
      <div className="tabular-nums">© 2026</div>
    </div>
  </SlideShell>
);

// -------------------------------------------------------------------
// SLIDE 2 — L'illusion parfaite d'une méga-flotte
// -------------------------------------------------------------------
const Slide2 = () => (
  <SlideShell testId="slide-2">
    <SlideHeader
      eyebrow="Notre proposition de valeur"
      title="L'illusion parfaite d'une méga-flotte intégrée."
      subtitle="Votre prolongement logistique pour reprendre le contrôle total — sans investir dans une flotte."
    />
    <div className="flex-1 flex items-center px-12 md:px-20 pb-20">
      <div className="grid md:grid-cols-3 gap-6 w-full">
        <PillarCard
          Icon={TrendingUp}
          accent="#047857"
          badge="Optimisation financière"
          title="-15% sur vos coûts d'expédition"
          body="Notre algorithme mutualise les retours à vide et choisit le véhicule optimal pour chaque mission."
        />
        <PillarCard
          Icon={Shield}
          accent="#D97706"
          badge="Sécurité · Zéro risque"
          title="Chaque kilo est assuré"
          body="Couverture « Tous Risques » via notre partenaire ASCOMA, sur 100% de vos marchandises."
        />
        <PillarCard
          Icon={Radio}
          accent="#0EA5E9"
          badge="Tour de contrôle"
          title="Visibilité centralisée 24/7"
          body="Fini les appels, SMS et emails dispersés. Une seule plateforme pour tout piloter."
        />
      </div>
    </div>
    <Footnote />
  </SlideShell>
);

// -------------------------------------------------------------------
// SLIDE 3 — Cycle de vie d'une expédition
// -------------------------------------------------------------------
const Slide3 = () => {
  const steps = [
    { Icon: CheckCircle2, num: 1, title: 'Confirmée', sub: 'Devis accepté' },
    { Icon: Calendar, num: 2, title: 'Planifiée', sub: 'Date + véhicule' },
    { Icon: Truck, num: 3, title: 'En cours', sub: 'GPS actif' },
    { Icon: FileCheck2, num: 4, title: 'Livrée', sub: 'BD signé' },
    { Icon: Receipt, num: 5, title: 'Facturée', sub: 'PDF envoyé' },
  ];
  return (
    <SlideShell testId="slide-3">
      <SlideHeader
        eyebrow="Vue d'ensemble"
        title="Le cycle de vie d'une expédition."
        subtitle="Un fil d'Ariane transparent. Chaque transition génère une notification — vous savez toujours où en est votre marchandise."
      />
      <div className="flex-1 flex items-center px-8 md:px-16 pb-20">
        <div className="w-full">
          <div className="flex items-start justify-between gap-2 md:gap-4 relative">
            {/* Ligne pointillée derrière */}
            <div className="absolute top-10 md:top-12 left-[10%] right-[10%] border-t-2 border-dashed border-[#047857]/40 -z-0" />
            {steps.map((s) => (
              <StepBubble key={s.num} {...s} />
            ))}
          </div>
        </div>
      </div>
      <Footnote />
    </SlideShell>
  );
};

// -------------------------------------------------------------------
// SLIDE 4 — Étape 1 : Activation & Connexion
// -------------------------------------------------------------------
const Slide4 = () => (
  <SlideShell testId="slide-4">
    <SlideHeader eyebrow="Étape 1 / 8" title="Activation & connexion." subtitle="Aucune installation. Compatible Chrome, Edge, Safari sur ordinateur, tablette et mobile." />
    <div className="flex-1 grid md:grid-cols-[1fr_1.1fr] gap-10 items-center px-12 md:px-20 pb-20">
      <div className="space-y-5">
        <div className="flex gap-4 items-start">
          <div className="w-12 h-12 rounded-lg bg-[#047857]/10 text-[#047857] flex items-center justify-center shrink-0">
            <Mail className="w-6 h-6" />
          </div>
          <div>
            <div className="font-bold text-lg text-[#0F172A]">1. Ouvrir l'email d'activation</div>
            <div className="text-[#475569] mt-1">
              Expéditeur <span className="font-mono text-sm">no-reply@baobabco.net</span> — cliquez sur « Activer mon compte ».
            </div>
          </div>
        </div>
        <div className="flex gap-4 items-start">
          <div className="w-12 h-12 rounded-lg bg-[#047857]/10 text-[#047857] flex items-center justify-center shrink-0">
            <LogIn className="w-6 h-6" />
          </div>
          <div>
            <div className="font-bold text-lg text-[#0F172A]">2. Définir votre mot de passe</div>
            <div className="text-[#475569] mt-1">12 caractères minimum, avec majuscule, chiffre et caractère spécial.</div>
          </div>
        </div>
        <div className="flex gap-4 items-start">
          <div className="w-12 h-12 rounded-lg bg-[#047857]/10 text-[#047857] flex items-center justify-center shrink-0">
            <Smartphone className="w-6 h-6" />
          </div>
          <div>
            <div className="font-bold text-lg text-[#0F172A]">3. Vous êtes connecté(e)</div>
            <div className="text-[#475569] mt-1">
              24/7 sur <span className="font-mono text-sm">app.baobabco.net</span>. Mot de passe oublié ? Récupération en 30 secondes.
            </div>
          </div>
        </div>
      </div>
      <ScreenshotFrame src={ASSET('img_001_01_login.png')} alt="Page de connexion Baobab Connect" caption="L'écran de connexion sur app.baobabco.net" />
    </div>
    <Footnote />
  </SlideShell>
);

// -------------------------------------------------------------------
// SLIDE 5 — Poste de Pilotage
// -------------------------------------------------------------------
const Slide5 = () => (
  <SlideShell testId="slide-5">
    <SlideHeader eyebrow="Étape 2 / 8" title="Votre Poste de Pilotage." subtitle="Comprendre votre situation logistique en 5 secondes." />
    <div className="flex-1 grid md:grid-cols-[1.4fr_1fr] gap-8 px-12 md:px-20 pb-20 items-center">
      <ScreenshotFrame src={ASSET('img_002_02_dashboard.png')} alt="Dashboard Baobab Connect" caption="Votre tableau de bord — vue d'ensemble en un coup d'œil" />
      <div className="space-y-4">
        <div className="bg-white border border-[#064E3B]/10 rounded-xl p-5">
          <div className="text-xs font-bold tracking-wider uppercase text-[#047857] mb-1">En haut</div>
          <div className="font-bold text-[#0F172A] mb-1">Vos KPIs immédiats</div>
          <div className="text-sm text-[#475569]">Opérations actives · Livrées ce mois · Retards en cours.</div>
        </div>
        <div className="bg-white border border-[#064E3B]/10 rounded-xl p-5">
          <div className="text-xs font-bold tracking-wider uppercase text-[#D97706] mb-1">Au centre</div>
          <div className="font-bold text-[#0F172A] mb-1">Activité 7 derniers jours</div>
          <div className="text-sm text-[#475569]">Comparez vos semaines en un graphique clair.</div>
        </div>
        <div className="bg-white border border-[#064E3B]/10 rounded-xl p-5">
          <div className="text-xs font-bold tracking-wider uppercase text-[#0EA5E9] mb-1">Bouton vert</div>
          <div className="font-bold text-[#0F172A] mb-1">Nouvelle demande</div>
          <div className="text-sm text-[#475569]">Accès direct à la création — toujours en haut à droite.</div>
        </div>
      </div>
    </div>
    <Footnote />
  </SlideShell>
);

// -------------------------------------------------------------------
// SLIDE 6 — Demander un Devis
// -------------------------------------------------------------------
const Slide6 = () => (
  <SlideShell testId="slide-6">
    <SlideHeader eyebrow="Étape 3 / 8" title="Demander un devis." subtitle="Exprimez votre besoin en 2 minutes. Réponse de notre commercial sous 10 à 40 minutes en heures ouvrées." />
    <div className="flex-1 grid md:grid-cols-[1.3fr_1fr] gap-8 items-center px-12 md:px-20 pb-20">
      <ScreenshotFrame src={ASSET('img_004_04_quote_create.png')} alt="Formulaire de demande de devis" caption="Le formulaire de demande de devis — 4 champs essentiels" />
      <div>
        <div className="bg-gradient-to-br from-[#FCD34D]/20 to-[#D97706]/15 border-2 border-[#D97706]/30 rounded-2xl p-7 mb-4">
          <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#D97706] mb-2">Promesse de service</div>
          <div className="text-5xl md:text-6xl font-extrabold text-[#0F172A] mb-2 tabular-nums">10–40 min</div>
          <div className="text-sm text-[#475569] leading-relaxed">
            Délai habituel de réponse en heures ouvrées. Hors heures, réponse le matin suivant.
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2 text-xs">
          {[
            { Icon: MapPin, t: 'Adresses' },
            { Icon: Package, t: 'Marchandise' },
            { Icon: Clock, t: 'Date & créneau' },
            { Icon: FileText, t: 'Instructions' },
          ].map(({ Icon, t }) => (
            <div key={t} className="flex items-center gap-2 bg-white border border-[#064E3B]/10 rounded-lg px-3 py-2">
              <Icon className="w-4 h-4 text-[#D97706]" />
              <span className="font-semibold text-[#0F172A]">{t}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
    <Footnote />
  </SlideShell>
);

// -------------------------------------------------------------------
// SLIDE 7 — Décision & Transparence
// -------------------------------------------------------------------
const Slide7 = () => (
  <SlideShell testId="slide-7">
    <SlideHeader eyebrow="Étape 4 / 8" title="Décision & transparence." subtitle="Finies les tarifications opaques. Trois actions claires, à un clic." />
    <div className="flex-1 grid md:grid-cols-[1.1fr_1fr] gap-8 px-12 md:px-20 pb-20 items-center">
      <ScreenshotFrame src={ASSET('img_103_devis_accept.png')} alt="Devis avec 3 boutons d'action" caption="L'écran de décision sur un devis : Accepter / Négocier / Refuser" />
      <div className="space-y-4">
        <div className="bg-white border-l-4 border-[#047857] rounded-r-lg p-4">
          <div className="text-xs font-bold tracking-wider uppercase text-[#047857] mb-1">Accepter</div>
          <div className="font-bold text-[#0F172A] mb-1">Création instantanée</div>
          <div className="text-sm text-[#475569]">L'opération passe en statut Planifiée. Le BL est envoyé au chauffeur.</div>
        </div>
        <div className="bg-white border-l-4 border-[#D97706] rounded-r-lg p-4">
          <div className="text-xs font-bold tracking-wider uppercase text-[#D97706] mb-1">Négocier</div>
          <div className="font-bold text-[#0F172A] mb-1">Demander une révision</div>
          <div className="text-sm text-[#475569]">Toute la négociation est tracée dans le devis. Aucune perte d'information.</div>
        </div>
        <div className="bg-white border-l-4 border-[#94A3B8] rounded-r-lg p-4">
          <div className="text-xs font-bold tracking-wider uppercase text-[#94A3B8] mb-1">Refuser</div>
          <div className="font-bold text-[#0F172A] mb-1">Clore la demande</div>
          <div className="text-sm text-[#475569]">Fermez avec un motif. Le commercial reçoit le retour pour mieux vous servir.</div>
        </div>
      </div>
    </div>
    <Footnote />
  </SlideShell>
);

// -------------------------------------------------------------------
// SLIDE 8 — Suivi GPS
// -------------------------------------------------------------------
const Slide8 = () => (
  <SlideShell testId="slide-8">
    <SlideHeader eyebrow="Étape 5 / 8" title="Suivi GPS en temps réel." subtitle="La fin de l'opacité sur la route." />
    <div className="flex-1 grid md:grid-cols-3 gap-6 px-12 md:px-20 pb-20 items-stretch">
      <PillarCard
        Icon={Satellite}
        accent="#0EA5E9"
        badge="Visibilité totale"
        title="Position en direct"
        body="Suivez la position du camion en direct sur la carte, partout, depuis votre téléphone ou ordinateur."
      />
      <PillarCard
        Icon={BellRing}
        accent="#D97706"
        badge="Proactivité"
        title="Alertes automatisées"
        body="Notifications de départ, franchissement de zones, arrivée à destination. Aucune surveillance manuelle."
      />
      <PillarCard
        Icon={HandHeart}
        accent="#047857"
        badge="Sérénité"
        title="Plus d'appels intempestifs"
        body="Inutile d'appeler le chauffeur toutes les heures. Vous savez, donc vous travaillez sereinement."
      />
    </div>
    <Footnote />
  </SlideShell>
);

// -------------------------------------------------------------------
// SLIDE 9 — Livraison & Traçabilité
// -------------------------------------------------------------------
const Slide9 = () => (
  <SlideShell testId="slide-9">
    <SlideHeader eyebrow="Étape 6 / 8" title="Livraison & traçabilité." subtitle="Les documents vitaux pour valider l'opération." />
    <div className="flex-1 grid md:grid-cols-[1fr_1.1fr] gap-8 px-12 md:px-20 pb-20 items-center">
      <ScreenshotFrame src={ASSET('img_105_sign_mobile.png')} alt="Signature mobile du bon de déchargement" caption="Le bon de déchargement signé au doigt sur smartphone" />
      <div className="bg-gradient-to-br from-[#FCD34D]/25 to-[#D97706]/20 border-2 border-[#D97706]/40 rounded-2xl p-8 md:p-10">
        <div className="flex items-center gap-3 mb-4">
          <AlertTriangle className="w-6 h-6 text-[#D97706]" />
          <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#D97706]">Règle d'or</div>
        </div>
        <h3 className="text-2xl md:text-3xl font-bold text-[#0F172A] leading-tight mb-3" style={{ fontFamily: 'Georgia, serif' }}>
          Conservez précieusement les reçus de pesée.
        </h3>
        <p className="text-[#475569] leading-relaxed">
          Les tickets de pont bascule prouvent le tonnage exact. Ils sont <strong>indispensables</strong> pour la clôture financière
          et la facturation correcte de chaque opération.
        </p>
      </div>
    </div>
    <Footnote />
  </SlideShell>
);

// -------------------------------------------------------------------
// SLIDE 10 — Messagerie Intégrée
// -------------------------------------------------------------------
const Slide10 = () => (
  <SlideShell testId="slide-10">
    <SlideHeader eyebrow="Étape 7 / 8" title="Messagerie intégrée." subtitle="Votre ligne directe avec le support Baobab Connect." />
    <div className="flex-1 grid md:grid-cols-3 gap-6 px-12 md:px-20 pb-20 items-stretch">
      <PillarCard
        Icon={MessageSquare}
        accent="#047857"
        badge="Contextuel"
        title="Chaque conversation est attachée à une opération"
        body="Plus de confusion : le support sait toujours de quelle expédition vous parlez."
      />
      <PillarCard
        Icon={FolderArchive}
        accent="#D97706"
        badge="Centralisé"
        title="Remplace SMS et emails éparpillés"
        body="Tout l'historique est conservé. Plus jamais de message perdu, plus de double-saisie."
      />
      <PillarCard
        Icon={ImageIcon}
        accent="#0EA5E9"
        badge="Pratique"
        title="Photos et PDF en un clic"
        body="Envoyez des photos du terrain ou des documents PDF directement depuis votre téléphone."
      />
    </div>
    <Footnote />
  </SlideShell>
);

// -------------------------------------------------------------------
// SLIDE 11 — Facturation & Finances
// -------------------------------------------------------------------
const Slide11 = () => (
  <SlideShell testId="slide-11">
    <SlideHeader eyebrow="Étape 8 / 8" title="Facturation & finances." subtitle="Toutes vos factures, paiements et relevés au même endroit." />
    <div className="flex-1 grid md:grid-cols-[1.2fr_1fr] gap-8 px-12 md:px-20 pb-20 items-center">
      <ScreenshotFrame src={ASSET('img_007_07_invoices.png')} alt="Centre de facturation" caption="Le centre de facturation : factures, paiements et relevés en un seul écran" />
      <div className="space-y-4">
        <div className="bg-white border-l-4 border-[#047857] rounded-r-lg p-4">
          <div className="text-xs font-bold tracking-wider uppercase text-[#047857] mb-1">Automatique</div>
          <div className="font-bold text-[#0F172A] mb-1">Factures PDF centralisées</div>
          <div className="text-sm text-[#475569]">Générées dès la livraison signée. Archivées 10 ans (OHADA).</div>
        </div>
        <div className="bg-white border-l-4 border-[#D97706] rounded-r-lg p-4">
          <div className="text-xs font-bold tracking-wider uppercase text-[#D97706] mb-1">Visuel</div>
          <div className="font-bold text-[#0F172A] mb-1">Suivi de votre encours</div>
          <div className="text-sm text-[#475569]">À régler · En retard · Payées : tout en couleurs et chiffres clairs.</div>
        </div>
        <div className="bg-white border-l-4 border-[#0EA5E9] rounded-r-lg p-4">
          <div className="text-xs font-bold tracking-wider uppercase text-[#0EA5E9] mb-1">Paiement facilité</div>
          <div className="font-bold text-[#0F172A] mb-1">Signalez vos règlements</div>
          <div className="text-sm text-[#475569]">Mobile Money, virement : déposez votre preuve. Rapprochement sous 48 h.</div>
        </div>
      </div>
    </div>
    <Footnote />
  </SlideShell>
);

// -------------------------------------------------------------------
// SLIDE 12 — Baobab sur le terrain (100% mobile)
// -------------------------------------------------------------------
const Slide12 = () => (
  <SlideShell testId="slide-12">
    <SlideHeader eyebrow="100% Mobile" title="Baobab Connect sur le terrain." subtitle="La logistique ne s'arrête pas au bureau." />
    <div className="flex-1 grid md:grid-cols-3 gap-6 px-12 md:px-20 pb-20 items-stretch">
      <PillarCard Icon={TabletSmartphone} accent="#047857" badge="Responsive" title="Interface tactile réactive" body="Le design s'adapte à votre écran : ordinateur, tablette ou téléphone. Aucune app à installer." />
      <PillarCard Icon={Camera} accent="#D97706" badge="In situ" title="Photos directement depuis l'entrepôt" body="Documentez l'état des marchandises au chargement et au déchargement, en un instant." />
      <PillarCard Icon={PenLine} accent="#0EA5E9" badge="Signature digitale" title="BL/BD signés au doigt" body="Sur l'écran de votre téléphone. Plus jamais besoin de ramener un bon papier au bureau." />
    </div>
    <Footnote />
  </SlideShell>
);

// -------------------------------------------------------------------
// SLIDE 13 — Golden Rules
// -------------------------------------------------------------------
const Slide13 = () => (
  <SlideShell testId="slide-13">
    <SlideHeader eyebrow="Bonnes pratiques" title="Les Golden Rules opérationnelles." subtitle="Pour un partenariat fluide et un succès garanti." />
    <div className="flex-1 grid md:grid-cols-3 gap-6 px-12 md:px-20 pb-20 items-stretch">
      <PillarCard Icon={PhoneCall} accent="#047857" badge="À retenir · Communication" title="Restez joignable" body="Gardez votre téléphone allumé lors des phases de chargement et de déchargement." />
      <PillarCard Icon={BellRing} accent="#D97706" badge="À retenir · Proactivité" title="Prévenez sans attendre" body="En cas d'imprévu (retard, blocage), envoyez-nous un message via le chat de l'opération." />
      <PillarCard Icon={Scale} accent="#0EA5E9" badge="À retenir · Conformité" title="Respectez volumes & tonnages" body="Respectez strictement les quantités annoncées dans le devis pour éviter les pénalités." />
    </div>
    <Footnote />
  </SlideShell>
);

// -------------------------------------------------------------------
// SLIDE 14 — Amélioration continue
// -------------------------------------------------------------------
const Slide14 = () => (
  <SlideShell testId="slide-14">
    <SlideHeader eyebrow="Notre engagement" title="L'amélioration continue." subtitle="Au-delà du logiciel, un accompagnement stratégique. Nous ne nous contentons pas de livrer." />
    <div className="flex-1 grid md:grid-cols-2 gap-10 px-12 md:px-20 pb-20 items-center">
      <div className="space-y-5">
        <div className="flex gap-4 items-start">
          <div className="w-12 h-12 rounded-lg bg-[#047857]/10 text-[#047857] flex items-center justify-center shrink-0">
            <Sparkles className="w-6 h-6" />
          </div>
          <div>
            <div className="font-bold text-lg text-[#0F172A]">Reportings mensuels</div>
            <div className="text-[#475569] mt-1">Accès à vos KPIs logistiques clés : volumétrie, ponctualité, coût par tonne.</div>
          </div>
        </div>
        <div className="flex gap-4 items-start">
          <div className="w-12 h-12 rounded-lg bg-[#D97706]/10 text-[#D97706] flex items-center justify-center shrink-0">
            <Target className="w-6 h-6" />
          </div>
          <div>
            <div className="font-bold text-lg text-[#0F172A]">Analyse de flux</div>
            <div className="text-[#475569] mt-1">
              Nos experts étudient vos expéditions pour optimiser vos routes futures et consolider vos gains financiers.
            </div>
          </div>
        </div>
      </div>
      <div className="bg-gradient-to-br from-[#047857] to-[#064E3B] rounded-2xl p-10 text-white text-center">
        <TrendingUp className="w-12 h-12 text-[#FCD34D] mx-auto mb-4" />
        <div className="text-5xl md:text-6xl font-extrabold text-[#FCD34D] mb-2 tabular-nums">ROI ↗</div>
        <div className="text-lg text-[#A7F3D0]">Gain financier croissant — votre logistique progresse avec vous.</div>
      </div>
    </div>
    <Footnote />
  </SlideShell>
);

// -------------------------------------------------------------------
// SLIDE 15 — Finale + Contact
// -------------------------------------------------------------------
const Slide15 = () => (
  <SlideShell testId="slide-15" dark className="overflow-hidden">
    <div className="absolute inset-0 opacity-[0.05]" style={{ backgroundImage: 'radial-gradient(circle at 70% 30%, #FCD34D 0%, transparent 50%)' }} />
    <div className="relative z-10 flex-1 flex flex-col justify-center px-12 md:px-24">
      <Quote className="w-12 h-12 text-[#FCD34D] mb-6" strokeWidth={1.5} />
      <blockquote
        className="text-4xl md:text-6xl lg:text-7xl font-extrabold leading-[1.1] text-white max-w-5xl"
        style={{ fontFamily: 'Georgia, "Playfair Display", serif' }}
      >
        La logistique est le moteur de l'Afrique.
        <br />
        <span className="text-[#FCD34D]">Nous en sommes le système d'exploitation.</span>
      </blockquote>
      <div className="mt-12 grid md:grid-cols-3 gap-6 max-w-4xl">
        <div className="text-[#A7F3D0]">
          <div className="text-xs uppercase tracking-wider font-bold text-[#FCD34D] mb-1">Web</div>
          <div className="font-mono text-sm">app.baobabco.net</div>
        </div>
        <div className="text-[#A7F3D0]">
          <div className="text-xs uppercase tracking-wider font-bold text-[#FCD34D] mb-1">Support</div>
          <div className="font-mono text-sm">support@baobabco.net</div>
        </div>
        <div className="text-[#A7F3D0]">
          <div className="text-xs uppercase tracking-wider font-bold text-[#FCD34D] mb-1">Centre d'aide</div>
          <div className="text-sm">Intégré à la plateforme</div>
        </div>
      </div>
    </div>
    <Footnote dark />
  </SlideShell>
);

// -------------------------------------------------------------------
// Export final : la liste ordonnée des 15 slides
// -------------------------------------------------------------------
export const ONBOARDING_SLIDES = [
  { id: 1, title: 'Bienvenue', Component: Slide1 },
  { id: 2, title: 'Méga-flotte intégrée', Component: Slide2 },
  { id: 3, title: 'Cycle de vie', Component: Slide3 },
  { id: 4, title: 'Activation & Connexion', Component: Slide4 },
  { id: 5, title: 'Poste de Pilotage', Component: Slide5 },
  { id: 6, title: 'Demander un devis', Component: Slide6 },
  { id: 7, title: 'Décision & Transparence', Component: Slide7 },
  { id: 8, title: 'Suivi GPS', Component: Slide8 },
  { id: 9, title: 'Livraison & Traçabilité', Component: Slide9 },
  { id: 10, title: 'Messagerie intégrée', Component: Slide10 },
  { id: 11, title: 'Facturation & Finances', Component: Slide11 },
  { id: 12, title: '100% Mobile', Component: Slide12 },
  { id: 13, title: 'Golden Rules', Component: Slide13 },
  { id: 14, title: 'Amélioration continue', Component: Slide14 },
  { id: 15, title: 'Finale', Component: Slide15 },
];

export default ONBOARDING_SLIDES;
